import React from 'react'
import Game from './Game'
import {
  render, getConfig, assetsUrl, GameProvider, shuffle, CONTENT_TYPE, TYPE_QUESTIONS
} from '@educaplay/core'
import { initialize } from '@educaplay/store'
import { GAMEMODE, QUESTION_TYPE } from './utils/constants'
import { isUnansweredAnswer } from '@educaplay/store/utils/functions'
import { calculateCasePoints, checkTypeAnswer, groupSegmentsWithMap } from './utils/functions'
import classes from './styles/main.module.scss'
import VideoQuizConfigProvider from './components/VideoQuizConfigProvider'
import { initErrorMonitoring } from '@educaplay/core'

initErrorMonitoring("https://457b931b4d26692c2f5c707f08228eef@o4509038211104768.ingest.de.sentry.io/4509671505068112");

const gameImage = assetsUrl("VIDEOTEST/logoActivity.png");
const container = document.getElementById('root');
const MAX_POINTS = 100;

const VIDEOQUIZ_SOUNDS = {
  "write": assetsUrl('VIDEOTEST/escribir.mp3'),
  "delete": assetsUrl('VIDEOTEST/escalar.mp3'),
  "submit": assetsUrl('VIDEOTEST/activar.mp3'),
  "charge": assetsUrl('VIDEOTEST/carga.mp3'),
  "move": assetsUrl('VIDEOTEST/mover.mp3'),
}

async function init() {
  const { data, texts, settings, user, title, description, author, id, lang, url } = await getConfig().catch((err) =>
    console.error('Error al obtener la configuración: ', err)
  )
  const { resources } = settings
  const isCaseSensitive = data.SENSIBLE_MAYUSCULAS_ACENTOS === 'si';
  const gameMode = data.EVITAR_SALTOS === "si" ? GAMEMODE.SEQUENTIAL : GAMEMODE.FREEDOM;
  const subtitle = {
    show: (data.OCULTAR_SUBTITULOS ?? "no") === "si" ? false : true,
    lang
  }

  const videoSources = data.VIDEO_LIBRARY.map((videoObject, index) => {
    const SOURCES = {
      youtube: "https://www.youtube-nocookie.com/watch?v=",
      vimeo: "https://vimeo.com/",
      local: resources
    }
    return {
      url: SOURCES[videoObject.MULTIMEDIA_TIPO] + videoObject.MULTIMEDIA,
      id: index
    }
  })

  const penalty = 0; //¿va a haber penalización extra?

  const formattedSequences = groupSegmentsWithMap(data.VIDEOS);

  const sequences = formattedSequences.map((sequence, index) => {
    return {
      groupOfIndex: sequence.OriginalGroupIndex,
      video: videoSources.find(video => video.id === sequence.IDVIDEO).url,
      id: index,
      secondStart: Number(sequence.SEGINI),
      timeAppear: formattedSequences.slice(0, index).reduce((acc, sequence) => {
        return acc + (sequence.SEGFIN - sequence.SEGINI)
      }, 0)
    }
  })

  const totalDuration = formattedSequences.reduce((acc, sequence) => {
    return acc + (Number(sequence.SEGFIN) - Number(sequence.SEGINI))
  }, 0);

  const filteredQuestions = data.PREGUNTAS
    .map((question, index) => ({ ...question, id: index }))
    .filter((question) => question.ENUNCIADO !== "" || question.MULTIMEDIA)
    .map(question => {
      return {
        ...question,
        RESPUESTAS: question.RESPUESTAS?.filter(response => response.RESPUESTA !== "" || response.MULTIMEDIA)
      }
    })
    .filter((question) => question.ESCRITA !== "si" || (question.ESCRITA === "si" && question.RESPUESTAS?.some(response => response.RESPUESTA !== "")))
    .filter((question) => question.ESCRITA === "si" || ((question.ESCRITA !== "si" && question.RESPUESTAS?.length >= 2 && question.RESPUESTAS?.some(response => response.RESPUESTA_CORRECTA === "si")) || question.INFO === "si"))
    .map((question) => {
      const segment = sequences.find(sequence => sequence.groupOfIndex.includes(question.SEGMENTO));
      const segmentsID = segment.groupOfIndex.slice(0, segment.groupOfIndex.indexOf(question.SEGMENTO));
      const accumulatedTime = data.VIDEOS.filter((sequence, index) => segmentsID.includes(index)).reduce((acc, sequence) => {
        return acc + (Number(sequence.SEGFIN) - Number(sequence.SEGINI))
      }, 0);
      const time = accumulatedTime + Number(question.TIEMPO);
      return {
        ...question,
        SEGMENTO: segment.id,
        TIEMPO: time,
      }
    })

  const sizeLetterQuestion = filteredQuestions.reduce((max, question) => {
    const maxRespuesta = question.RESPUESTAS.reduce((response, responseObj) => {
      return Math.max(response, responseObj.RESPUESTA.length);
    }, 0);

    return Math.max(max, maxRespuesta);
  }, 0);

  const questions = filteredQuestions.map((question, index) => {
    const numQuestionsWithAnswer = filteredQuestions.filter(question => question.INFO !== "si").length;
    const points = MAX_POINTS / numQuestionsWithAnswer;
    const questionType = question.INFO === "si" ? QUESTION_TYPE.Info : (question.ESCRITA === "si") ? QUESTION_TYPE.TextQuestion : (question.MULTIPLE === "si") ? QUESTION_TYPE.MultiQuestion : QUESTION_TYPE.SingleQuestion;
    const interactionTypeQuestion = questionType === QUESTION_TYPE.TextQuestion ? TYPE_QUESTIONS.Text : questionType === QUESTION_TYPE.Info ? TYPE_QUESTIONS.Information : TYPE_QUESTIONS.Selection;
    const answers = question.RESPUESTAS ? shuffle(question.RESPUESTAS?.map((answer, index) => {
      return {
        answer: answer.RESPUESTA,
        source: answer.MULTIMEDIA ? `${resources}${answer.MULTIMEDIA}` : null,
        type: answer.MULTIMEDIA_TIPO ?? null,
        correctAnswer: questionType === QUESTION_TYPE.TextQuestion ? true : answer.RESPUESTA_CORRECTA === "si",
        id: index
      }
    })) : [];
    const correctAnswers = questionType === QUESTION_TYPE.Info ? [] : answers.filter(answer => answer.correctAnswer).map(answer => answer.id).sort((a, b) => { return a - b });


    const checkAnswer = (objAnswer) => {
      if (!objAnswer) return false;
      if (objAnswer?.type === TYPE_QUESTIONS.Information && objAnswer?.answer === true) return true;
      if (isUnansweredAnswer(objAnswer?.answer)) return false;
      const answer = Array.isArray(objAnswer.answer) ? [...objAnswer.answer].sort((a, b) => { return a - b }) : objAnswer.answer;

      const formattedCorrectAnswers = questionType === QUESTION_TYPE.TextQuestion ? correctAnswers.map(answerID => answers.find(answer => answer.id === answerID).answer) : correctAnswers;
      return checkTypeAnswer({ sortedAnswered: answer, correctAnswers: formattedCorrectAnswers, questionType: objAnswer.type, isCaseSensitive });
    }

    const calculatePoints = (objAnswer) => {
      if (!objAnswer) return 0;
      const sortedAnswered = Array.isArray(objAnswer.answer) ? [...objAnswer.answer].sort((a, b) => { return a - b }) : objAnswer.answer;
      const textCorrectAnswers = correctAnswers.map(answerID => answers.find(answer => answer.id === answerID).answer);
      const checkAnswers = questionType === QUESTION_TYPE.TextQuestion ? textCorrectAnswers : correctAnswers;
      return calculateCasePoints({ sortedAnswered, correctAnswers: checkAnswers, question, points, questionType, isCaseSensitive, penalty });
    }
    const time = question.TIEMPO;
    const previewQuestion = question.ADELANTAR_ENUNCIADO === 'si' && question.TIEMPO_ENUNCIADO > 0;
    const originalTimeAppear = sequences.find(sequence => sequence.id === question.SEGMENTO).timeAppear + time;
    const previousQuestionTime = isNaN(sequences.find(sequence => sequence.id === filteredQuestions[index - 1]?.SEGMENTO)?.timeAppear + filteredQuestions[index - 1]?.TIEMPO) ? 0 : sequences.find(sequence => sequence.id === filteredQuestions[index - 1]?.SEGMENTO)?.timeAppear + filteredQuestions[index - 1]?.TIEMPO;
    const limitDuration = originalTimeAppear - previousQuestionTime;
    const questionPrevTime = Math.min(question.TIEMPO_ENUNCIADO, limitDuration);
    const timeAppear = originalTimeAppear - (previewQuestion ? questionPrevTime : 0);
    const timeAppearWithDuration = previewQuestion ? timeAppear + questionPrevTime : null;

    return {
      timeAppear,
      timeAppearWithDuration,
      bank: (question.ENUNCIADO || question.MULTIMEDIA) ? {
        text: question.ENUNCIADO,
        source: question.MULTIMEDIA_TIPO === CONTENT_TYPE.YOUTUBE ? question.MULTIMEDIA : `${resources}${question.MULTIMEDIA}`,
        type: question.MULTIMEDIA_TIPO ?? CONTENT_TYPE.TEXT
      } : null,
      answers,
      questionType,
      subtitle,
      info: questionType === QUESTION_TYPE.Info ? question.ENUNCIADO : null,
      interactionTypeQuestion,
      correctAnswers,
      feedback: (question.EXPLICACION_ACTIVA === "si") ? question.EXPLICACION ?? null : null,
      checkAnswer,
      calculatePoints,
      id: question.id,
      points,
      sequences,
      totalDuration,
      answer: null,
      videoSources,
      text: question.ENUNCIADO,
      media: question.MULTIMEDIA ? {
        type: question.MULTIMEDIA_TIPO,
        src: question.MULTIMEDIA_TIPO === CONTENT_TYPE.YOUTUBE ? question.MULTIMEDIA : `${resources}${question.MULTIMEDIA}`,
      } : null,
    }
  })

  initialize({
    lives: data.NUMERO_INTENTOS, //COMPROBAR SI ES MAXIMO
    liveActived: data.NUMERO_INTENTOS_INFINITOS !== "si",
    hasCountDown: false,
    randomized: data.ALEATORIO === "si",
    showSolutions: data.OCULTAR_RESPUESTAS !== "1",
    showRestart: data.OCULTAR_REINICIAR !== "1",
    showSocialMedia: data.OCULTAR_REDES !== "1",
    finalText: data.TEXTO_FINAL,
    automaticallyFinishGame: false,
    showFeedbackGame: true,
    showUserAnswer: true,
    hasBank: false,
    hasProgressBar: { view: true, onlyCorrect: false },
    semiCorrect: true,
    questions,
    settings,
    title,
    description,
    author,
    id,
    url,
    user,
    customizations: {
      customLogo: data.LOGO,
      customBarLogo: data.FRANJA,
      backgroundImage: { src: data.BACKGROUND, showGame: false },
      colors: {
        primary: data.COLOR_FONDO,
        text: data.COLOR_FUENTE,
        action: data.COLOR_BOTONES,
        textAction: data.COLOR_FUENTE_B,
      }
    }
  });


  render(container, (
    // <React.StrictMode>
    <VideoQuizConfigProvider gameMode={gameMode} sizeLetterQuestion={sizeLetterQuestion}>
      <GameProvider
        classes={{
          screen: classes.screen,
          layout: classes.layout,
        }}
        gameImage={gameImage}
        texts={texts}
        Game={Game}
        sounds={VIDEOQUIZ_SOUNDS}
        mappers={{
          report: (question) => {
            if (question.questionType === QUESTION_TYPE.Info) return null;
            const isCorrect = question.checkAnswer(question.answer)

            const formattedAnswer = Array.isArray(question.answer.answer)
              ? question.answer.answer.map(Number)
              : [Number(question.answer.answer)]

            return {
              s: isCorrect,
              i: question.id,
              a: question.questionType === QUESTION_TYPE.TextQuestion ? question.answer.answer : formattedAnswer
            }
          }
        }}
        renders={{
          infoRight: (_currentIndex, questions) => {
            const answeredQuestions = questions.filter(question => (
              !isUnansweredAnswer(question.answer))).length;
            return `${texts.common.game.question}: ${answeredQuestions}/${questions.length} `
          }
        }}
      />
    </VideoQuizConfigProvider>
    // </React.StrictMode>
  ))
}

init();